import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { generateEmployeePin, setEmployeePin, getEmployeePin, createPinRecovery } from '@/integrations/supabase/client';
import { Key, Eye, EyeOff, RefreshCw } from 'lucide-react';

interface PinManagementProps {
  profileId: string;
  employeeName: string;
  currentPin?: string;
  onPinUpdated: (newPin: string) => void;
}

export default function PinManagement({ 
  profileId, 
  employeeName, 
  onPinUpdated 
}: PinManagementProps) {
  const [showPin, setShowPin] = useState(false);
  const [customPin, setCustomPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedPin, setGeneratedPin] = useState<string | null>(null);
  const [currentPinStatus, setCurrentPinStatus] = useState<{ hasPin: boolean; pin?: string }>({ hasPin: false });
  const { toast } = useToast();

  useEffect(() => {
    checkPinStatus();
  }, [profileId]);

  const getManagerPinOrThrow = () => {
    const pin = typeof window !== 'undefined' ? window.localStorage.getItem('manager_pin') : null;
    if (!pin) throw new Error('PIN manager manquant');
    return pin;
  };

  const shouldUsePinMode = () => {
    if (typeof window === 'undefined') return false;
    const authMethod = window.localStorage.getItem('auth_method');
    const managerPin = window.localStorage.getItem('manager_pin');
    // Si on est en mode PIN et qu'on a un PIN manager, utiliser le mode PIN
    // Le backend validera le PIN via pin_require_manager_for_pin_mgmt
    return authMethod === 'pin' && Boolean(managerPin);
  };

  const checkPinStatus = async () => {
    try {
      if (shouldUsePinMode()) {
        // Mode PIN : utiliser la RPC PIN mode
        const pin = getManagerPinOrThrow();
        const { data, error } = await supabase.rpc('pin_employee_pin_status', { p_pin: pin, p_profile_id: profileId });
        if (error) {
          console.error('Erreur lors de la vérification du statut PIN (mode PIN):', error);
          setCurrentPinStatus({ hasPin: false });
          return;
        }
        setCurrentPinStatus({ hasPin: Boolean(data), pin: undefined });
      } else {
        // Mode JWT (Supabase auth) : utiliser la table profiles directement
        const { data, error } = await supabase
          .from('profiles')
          .select('pin_hash, pin_digest')
          .eq('id', profileId)
          .single();
        if (error) {
          console.error('Erreur lors de la vérification du statut PIN (mode JWT):', error);
          setCurrentPinStatus({ hasPin: false });
          return;
        }
        setCurrentPinStatus({
          hasPin: !!(data?.pin_hash && data?.pin_digest),
          pin: undefined, // On ne stocke jamais le PIN en clair
        });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut PIN:', error);
      setCurrentPinStatus({ hasPin: false });
    }
  };

  const handleGeneratePin = async () => {
    setLoading(true);
    try {
      // Vérifier qu'on a bien un PIN manager en mode PIN
      if (shouldUsePinMode()) {
        const { data, error } = await supabase.rpc('pin_generate_employee_pin', { 
          p_pin: getManagerPinOrThrow(), 
          p_profile_id: profileId 
        });
        if (error) throw error;
        if (data) {
          setGeneratedPin(String(data));
          onPinUpdated(String(data));
          await checkPinStatus();
          toast({
            title: "PIN généré",
            description: `Nouveau PIN pour ${employeeName}: ${String(data)}`,
          });
          return;
        }
      } else {
        // Mode JWT (Supabase auth)
        const { data, error } = await generateEmployeePin(profileId);
        if (error) throw error;
        
        if (data) {
          setGeneratedPin(String(data));
          onPinUpdated(String(data));
          
          // Mettre à jour le statut
          await checkPinStatus();
          
          toast({
            title: "PIN généré",
            description: `Nouveau PIN pour ${employeeName}: ${String(data)}`,
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors de la génération du PIN:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Impossible de générer le PIN",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetCustomPin = async () => {
    if (!customPin || customPin.length !== 6 || !/^\d{6}$/.test(customPin)) {
      toast({
        title: "PIN invalide",
        description: "Le PIN doit contenir exactement 6 chiffres",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const res = shouldUsePinMode()
        ? await supabase.rpc('pin_set_employee_pin', { p_pin: getManagerPinOrThrow(), p_profile_id: profileId, p_new_pin: customPin })
        : await setEmployeePin(profileId, customPin);
      const error = (res as { error?: unknown }).error as any;
      
      if (error) throw error;
      
      onPinUpdated(customPin);
      setGeneratedPin(customPin);
      setCustomPin('');
      
      // Mettre à jour le statut
      await checkPinStatus();
      
      toast({
        title: "PIN défini",
        description: `PIN personnalisé défini pour ${employeeName}`,
      });
    } catch (error) {
      console.error('Erreur lors de la définition du PIN:', error);
      toast({
        title: "Erreur",
        description: "Impossible de définir le PIN",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePin = async () => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le PIN de ${employeeName} ?`)) {
      return;
    }

    setLoading(true);
    try {
      const res = shouldUsePinMode()
        ? await supabase.rpc('pin_remove_employee_pin', { p_pin: getManagerPinOrThrow(), p_profile_id: profileId })
        : await supabase
            .from('profiles')
            .update({
              pin_hash: null,
              pin_digest: null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', profileId);
      const error = (res as any).error;

      if (error) throw error;
      
      setGeneratedPin(null);
      await checkPinStatus();
      
      toast({
        title: "PIN supprimé",
        description: `Le PIN de ${employeeName} a été supprimé`,
      });
    } catch (error) {
      console.error('Erreur lors de la suppression du PIN:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le PIN",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetExistingPin = async () => {
    setLoading(true);
    try {
      const { data, error } = shouldUsePinMode()
        ? await supabase.rpc('pin_get_employee_pin', { p_pin: getManagerPinOrThrow(), p_profile_id: profileId })
        : await getEmployeePin(profileId);
      
      if (error) throw error;
      
      if (data) {
        setGeneratedPin(String(data));
        toast({
          title: "PIN récupéré",
          description: `PIN actuel de ${employeeName}: ${String(data)}`,
        });
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du PIN:', error);
      toast({
        title: "Erreur",
        description: "Impossible de récupérer le PIN existant",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePinRecovery = async () => {
    if (!confirm(`Créer un nouveau PIN de récupération pour ${employeeName} ? L'ancien PIN sera remplacé.`)) {
      return;
    }

    setLoading(true);
    try {
      const { data, error } = shouldUsePinMode()
        ? await supabase.rpc('pin_create_pin_recovery', { p_pin: getManagerPinOrThrow(), p_profile_id: profileId })
        : await createPinRecovery(profileId);
      
      if (error) throw error;
      
      if (data) {
        setGeneratedPin(String(data));
        await checkPinStatus();
        
        toast({
          title: "PIN de récupération créé",
          description: `Nouveau PIN pour ${employeeName}: ${String(data)}`,
        });
      }
    } catch (error) {
      console.error('Erreur lors de la création du PIN de récupération:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer le PIN de récupération",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const displayPin = generatedPin;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="w-5 h-5" />
          Gestion du PIN
        </CardTitle>
        <CardDescription>
          Gérer l'accès par PIN pour {employeeName}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Statut PIN actuel */}
        <div className="space-y-2">
          <Label>Statut PIN actuel</Label>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={currentPinStatus.hasPin ? "default" : "secondary"}>
                {currentPinStatus.hasPin ? "PIN configuré" : "Aucun PIN"}
              </Badge>
              {currentPinStatus.hasPin && (
                <span className="text-sm text-muted-foreground">
                  L'employé peut se connecter avec un PIN
                </span>
              )}
            </div>
            {currentPinStatus.hasPin && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRemovePin}
                disabled={loading}
              >
                Supprimer PIN
              </Button>
            )}
          </div>
          {currentPinStatus.hasPin && (
            <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
              💡 <strong>Conseil :</strong> Utilisez "Voir PIN actuel" pour récupérer le PIN existant, 
              ou "Créer récupération" pour générer un nouveau PIN si l'employé a oublié le sien.
            </div>
          )}
        </div>

        {/* PIN généré/récemment défini */}
        {displayPin && (
          <div className="space-y-2">
            <Label>PIN récemment défini</Label>
            <div className="flex items-center gap-2">
              <Input
                type={showPin ? 'text' : 'password'}
                value={displayPin}
                readOnly
                className="font-mono text-lg tracking-widest"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowPin(!showPin)}
              >
                {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <Badge variant="secondary" className="text-xs">
              {showPin ? 'PIN visible' : 'PIN masqué'} - Notez ce PIN avant de fermer cette fenêtre
            </Badge>
          </div>
        )}

        {/* Actions sur PIN existant */}
        {currentPinStatus.hasPin && (
          <div className="space-y-2">
            <Label>Actions sur PIN existant</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleGetExistingPin}
                disabled={loading}
                variant="outline"
                size="sm"
              >
                Voir PIN actuel
              </Button>
              <Button
                onClick={handleCreatePinRecovery}
                disabled={loading}
                variant="outline"
                size="sm"
              >
                Créer récupération
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              "Voir PIN actuel" récupère le PIN existant.<br/>
              "Créer récupération" génère un nouveau PIN et remplace l'ancien.
            </p>
          </div>
        )}

        {/* Génération automatique */}
        <div className="space-y-2">
          <Label>Générer un PIN automatiquement</Label>
          <Button
            onClick={handleGeneratePin}
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Générer un nouveau PIN
          </Button>
        </div>

        {/* PIN personnalisé */}
        <div className="space-y-2">
          <Label>Définir un PIN personnalisé</Label>
          <div className="flex gap-2">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              minLength={6}
              value={customPin}
              onChange={(e) => setCustomPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••••"
              className="font-mono tracking-widest"
            />
            <Button
              onClick={handleSetCustomPin}
              disabled={loading || customPin.length !== 6}
              variant="outline"
            >
              Définir
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Le PIN doit contenir exactement 6 chiffres
          </p>
        </div>

        {/* Instructions */}
        <div className="p-3 bg-muted rounded-md">
          <p className="text-sm text-muted-foreground">
            <strong>Note :</strong> Le PIN sera visible une seule fois lors de la génération. 
            Assurez-vous de le communiquer à l'employé de manière sécurisée.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
